import React from 'react';
import { motion } from 'framer-motion';
import { Folder, BookOpen } from 'lucide-react';
import { Category } from '../types';
import { cn } from '../utils/cn';

interface FolderCardProps {
  category: Category;
  onClick: () => void;
  tutorialCount?: number;
  className?: string;
}

export const FolderCard: React.FC<FolderCardProps> = ({
  category,
  onClick,
  tutorialCount = 0,
  className
}) => {
  const iconContent = category.icon ? (
    <span className="text-xl">{category.icon}</span>
  ) : (
    <Folder className="w-6 h-6" />
  );

  return (
    <motion.div
      whileHover={{ y: -4, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={cn(
        "bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer border-2 border-gray-100 hover:border-primary-200 group relative overflow-hidden",
        className
      )}
    >
      {/* Subtle gradient overlay on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-50/0 to-primary-50/0 group-hover:from-primary-50/50 group-hover:to-transparent transition-all duration-300 pointer-events-none" />
      
      <div className="relative flex items-start space-x-4">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110"
          style={{
            backgroundColor: category.color ? `${category.color}15` : '#667eea15',
            color: category.color || '#667eea',
            border: `2px solid ${category.color ? `${category.color}30` : '#667eea30'}`
          }}
        >
          {iconContent}
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors leading-tight">
            {category.name}
          </h3>

          {category.description && (
            <p className="text-sm text-gray-600 mb-3 line-clamp-2 leading-relaxed">
              {category.description}
            </p>
          )}

          <div className="flex items-center text-sm font-semibold text-gray-700 bg-gray-50 px-3 py-1.5 rounded-lg w-fit group-hover:bg-primary-50 group-hover:text-primary-700 transition-colors">
            <BookOpen className="w-4 h-4 mr-1.5" />
            <span>{tutorialCount} {tutorialCount === 1 ? 'tutorial' : 'tutorials'}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

